"use client";

import {
  AnimatePresence,
  type Transition,
  motion,
  useReducedMotion,
} from "motion/react";

import {
  type FocusEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { MdOutlineMarkEmailRead } from "react-icons/md";

import { Checkbox } from "@/components/ui";
import { BulkActionsBar } from "@/components/ui/bulk-actions-bar";

import { useUser } from "@/contexts/user-provider";

import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { useToast } from "@/lib/use-toast";

import { ChatFilters } from "@/types/chat";

import { useMarkAllChatsReadMutation } from "@/query/comms/chat";

const SPRING: Transition = {
  type: "spring",
  stiffness: 380,
  damping: 32,
  mass: 0.7,
};
const NO_MOTION: Transition = { duration: 0 };

export interface ChatSelection {
  canSelect: boolean;
  selectedIds: Set<string>;
  /** Something is ticked, so every row shows its checkbox. */
  isSelecting: boolean;
  allSelected: boolean;
  toggle: (chatId: string) => void;
  toggleAll: () => void;
  clear: () => void;
}

/**
 * Which chats are ticked in the sidebar list. Cleared whenever the filters
 * change, so a selection never covers chats the list no longer shows.
 */
export function useChatSelection(
  chatIds: string[],
  filters: ChatFilters,
): ChatSelection {
  const { currentUser } = useUser();
  const canSelect = hasPermission(
    currentUser,
    PERMISSIONS.MESSAGING_CHATS_WRITE,
  );
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Set by "Select all" and kept until something is unticked, so chats that load
  // further down the list join the selection instead of quietly undoing it.
  const [selectAllMode, setSelectAllMode] = useState(false);

  // Cleared during render, not in an effect, when the filters change: an effect
  // would run alongside the select-all sync below, which still sees the old
  // select-all mode and would tick the new filter's chats.
  const filterKey = JSON.stringify([
    filters.searchTerm,
    filters.unreadOnly,
    filters.unrespondedOnly,
    filters.humanOnly,
    filters.locationId,
  ]);
  const [selectionFilterKey, setSelectionFilterKey] = useState(filterKey);
  if (filterKey !== selectionFilterKey) {
    setSelectionFilterKey(filterKey);
    setSelectedIds(new Set());
    setSelectAllMode(false);
  }

  useEffect(() => {
    if (!selectAllMode) return;
    setSelectedIds((previous) =>
      chatIds.every((id) => previous.has(id))
        ? previous
        : new Set([...Array.from(previous), ...chatIds]),
    );
  }, [selectAllMode, chatIds]);

  const toggle = useCallback(
    (chatId: string) => {
      if (selectedIds.has(chatId)) setSelectAllMode(false);
      setSelectedIds((previous) => {
        const next = new Set(previous);
        if (next.has(chatId)) next.delete(chatId);
        else next.add(chatId);
        return next;
      });
    },
    [selectedIds],
  );

  const allSelected =
    selectAllMode ||
    (chatIds.length > 0 && chatIds.every((id) => selectedIds.has(id)));

  const toggleAll = useCallback(() => {
    setSelectedIds(allSelected ? new Set() : new Set(chatIds));
    setSelectAllMode(!allSelected);
  }, [allSelected, chatIds]);

  const clear = useCallback(() => {
    setSelectedIds(new Set());
    setSelectAllMode(false);
  }, []);

  // Only ticked chats still in the list: one read on another console leaves the
  // list (realtime prune) but would otherwise stay counted in "N selected".
  const visibleSelectedIds = useMemo(() => {
    const visible = new Set(chatIds);
    return new Set(Array.from(selectedIds).filter((id) => visible.has(id)));
  }, [selectedIds, chatIds]);

  return {
    canSelect,
    selectedIds: visibleSelectedIds,
    isSelecting: visibleSelectedIds.size > 0,
    allSelected,
    toggle,
    toggleAll,
    clear,
  };
}

/** Hover or keyboard focus on a chat row, which reveals its checkbox. */
export function useRowReveal() {
  const [revealed, setRevealed] = useState(false);
  const handlers = useMemo(
    () => ({
      onMouseEnter: () => setRevealed(true),
      onMouseLeave: () => setRevealed(false),
      onFocus: () => setRevealed(true),
      onBlur: (event: FocusEvent<HTMLElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setRevealed(false);
      },
    }),
    [],
  );
  return { revealed, handlers };
}

interface RowCheckboxProps {
  label: string;
  checked: boolean;
  /** The row is hovered or focused. */
  revealed: boolean;
  isSelecting: boolean;
  /** Row position, to cascade the checkboxes in when selecting starts. */
  index: number;
  onToggle: () => void;
}

/**
 * Slides in from the left on hover and pushes the row's text over. Once a chat
 * is ticked every row shows its box, cascading down the list.
 */
export function RowCheckbox({
  label,
  checked,
  revealed,
  isSelecting,
  index,
  onToggle,
}: RowCheckboxProps) {
  const reduceMotion = useReducedMotion();
  const visible = revealed || isSelecting || checked;
  const cascadeDelay =
    isSelecting && !revealed ? Math.min(index, 12) * 0.03 : 0;

  return (
    <motion.div
      initial={false}
      animate={
        visible
          ? { width: 28, opacity: 1, x: 0 }
          : { width: 0, opacity: 0, x: -14 }
      }
      transition={reduceMotion ? NO_MOTION : { ...SPRING, delay: cascadeDelay }}
      className="flex shrink-0 items-start overflow-hidden pt-[3px]"
      // The row opens the chat on click; ticking the box must not.
      onClick={(event) => event.stopPropagation()}
    >
      <motion.div whileTap={reduceMotion ? undefined : { scale: 0.85 }}>
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          // Stays in the tab order while hidden: focusing it reveals it through
          // the row's focus handler, which is the only way in without a mouse.
          aria-label={label}
        />
      </motion.div>
    </motion.div>
  );
}

interface SelectAllRowProps {
  selection: ChatSelection;
}

/** Slides in from the right under the search box while chats are ticked. */
export function SelectAllRow({ selection }: SelectAllRowProps) {
  const reduceMotion = useReducedMotion();
  const count = selection.selectedIds.size;

  return (
    <AnimatePresence initial={false}>
      {selection.isSelecting && (
        <motion.div
          key="select-all"
          initial={{ opacity: 0, x: 56, height: 0, marginTop: 0 }}
          animate={{ opacity: 1, x: 0, height: "auto", marginTop: 12 }}
          exit={{ opacity: 0, x: 56, height: 0, marginTop: 0 }}
          transition={reduceMotion ? NO_MOTION : SPRING}
          className="overflow-hidden"
        >
          <div className="flex items-center justify-between gap-3 border-border p-3 pt-0">
            <label className="flex cursor-pointer items-center gap-2 text-body-strong text-foreground">
              <Checkbox
                checked={selection.allSelected ? true : "indeterminate"}
                onCheckedChange={selection.toggleAll}
                aria-label="Select all chats"
              />
              <span>Select all</span>
            </label>
            <span className="text-caption tabular-nums text-foreground-secondary">
              {count} selected
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * What "Mark as read" covers. After "Select all" it reads "Mark all as read".
 * When the list hasn't loaded every page and no search or narrowing filter is
 * on, it clears every unread chat on the server (scoped to My Location), so
 * nothing further down the list is missed. Otherwise it sends exactly the
 * ticked chats.
 */
export function markReadPlan({
  selectedIds,
  allSelected,
  hasMoreChats,
  filters,
}: {
  selectedIds: Set<string>;
  allSelected: boolean;
  hasMoreChats: boolean;
  filters: ChatFilters;
}): { label: string; chatIds: string[] | undefined } {
  const narrowed =
    !!filters.searchTerm || filters.unrespondedOnly || filters.humanOnly;
  const coversEverything = allSelected && (!hasMoreChats || !narrowed);
  const everythingOnServer = allSelected && hasMoreChats && !narrowed;
  return {
    label: coversEverything
      ? "Mark all as read"
      : `Mark ${selectedIds.size} as read`,
    chatIds: everythingOnServer ? undefined : Array.from(selectedIds),
  };
}

interface ChatBulkActionsProps {
  selection: ChatSelection;
  filters: ChatFilters;
  /** More pages exist that the list hasn't loaded. */
  hasMoreChats: boolean;
}

/**
 * The action bar for ticked chats, centred at the bottom of the page; see
 * `markReadPlan` for what it marks. Rendered into the body because the
 * Messages panel's blur and motion transforms would otherwise trap a fixed bar
 * inside the panel.
 */
export function ChatBulkActions({
  selection,
  filters,
  hasMoreChats,
}: ChatBulkActionsProps) {
  const { toast } = useToast();
  const { mutateAsync: markRead, isPending } = useMarkAllChatsReadMutation();
  const count = selection.selectedIds.size;

  const plan = markReadPlan({
    selectedIds: selection.selectedIds,
    allSelected: selection.allSelected,
    hasMoreChats,
    filters,
  });

  const actions = useMemo(
    () => [
      {
        id: "mark-read",
        label: plan.label,
        icon: <MdOutlineMarkEmailRead className="size-4" />,
        variant: "secondary" as const,
        onClick: async () => {
          try {
            const { chatIds } = await markRead({
              locationId: filters.locationId,
              chatIds: plan.chatIds,
            });
            selection.clear();
            toast({
              title:
                chatIds.length === 0
                  ? "Those chats were already read"
                  : `Marked ${chatIds.length} ${chatIds.length === 1 ? "chat" : "chats"} as read`,
              variant: "success",
            });
          } catch {
            toast({
              title: "Error",
              description: "Couldn't mark chats as read. Please try again.",
              variant: "error",
            });
          }
        },
      },
    ],
    [plan, markRead, filters.locationId, selection, toast],
  );

  if (count === 0 || typeof document === "undefined") return null;

  return createPortal(
    <BulkActionsBar
      selectedCount={count}
      itemLabel="chat"
      actions={actions}
      onClear={selection.clear}
      isLoading={isPending}
      ariaLabel="Chat actions"
      position="fixed"
      // Above the Messages panel, which sits at z-[99999].
      className="z-[100000]"
    />,
    document.body,
  );
}
